import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { format, parseISO } from 'date-fns';

const EmailPreview = ({ bookingData }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const formatDate = (dateString) => {
    try {
      const date = parseISO(dateString);
      return format(date, 'EEEE, MMMM dd, yyyy');
    } catch {
      return 'Date not available';
    }
  };

  const formatTime = (dateString) => {
    try {
      const date = parseISO(dateString);
      return format(date, 'h:mm a');
    } catch {
      return '';
    }
  };

  const formattedDate = formatDate(bookingData?.scheduledStart);
  const startTime = formatTime(bookingData?.scheduledStart);
  const endTime = formatTime(bookingData?.scheduledEnd);

  return (
    <section className="py-16 px-4 bg-white/5">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-premium overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#002B45] to-[#003D5C] p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-[#1FB5A5] rounded-lg flex items-center justify-center">
                  <Icon name="Mail" size={24} className="text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Email Confirmation Preview</h2>
                  <p className="text-white/70 text-sm">Sent to {bookingData?.clientEmail}</p>
                </div>
              </div>
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-white/80 hover:text-white transition-colors"
                aria-label={isExpanded ? 'Collapse email preview' : 'Expand email preview'}
              >
                <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={24} />
              </button>
            </div>
          </div>

          {/* Email Preview Content */}
          <div className={`transition-all duration-300 ${isExpanded ? 'max-h-[2000px]' : 'max-h-0'} overflow-hidden`}>
            <div className="p-8">
              {/* Email Header */}
              <div className="bg-gradient-to-r from-purple-600 to-purple-800 p-8 text-center rounded-t-xl mb-6">
                <h3 className="text-white text-3xl font-bold">Consultation Confirmed! 🎉</h3>
              </div>

              {/* Email Body */}
              <div className="bg-gray-50 p-8 rounded-b-xl">
                <p className="text-gray-800 text-base mb-5">
                  Hi {bookingData?.clientName},
                </p>
                
                <p className="text-gray-800 text-base mb-5">
                  Thank you for scheduling a consultation with us! We're excited to discuss how we can help{' '}
                  {bookingData?.company ? (
                    <strong>{bookingData?.company}</strong>
                  ) : (
                    'your organization'
                  )}{' '}
                  achieve its goals.
                </p>
                
                {/* Appointment Details Box */}
                <div className="bg-white p-6 rounded-lg border-l-4 border-purple-600 my-6 shadow-sm">
                  <h4 className="text-purple-600 text-xl font-bold mb-4">Appointment Details</h4>
                  
                  <table className="w-full">
                    <tbody>
                      <tr>
                        <td className="py-2 font-semibold text-gray-600 w-32">📅 Date:</td>
                        <td className="py-2 text-gray-800">{formattedDate}</td>
                      </tr>
                      <tr>
                        <td className="py-2 font-semibold text-gray-600">🕐 Time:</td>
                        <td className="py-2 text-gray-800">
                          {startTime}{endTime ? ` - ${endTime}` : ''}
                        </td>
                      </tr>
                      {bookingData?.title && (
                        <tr>
                          <td className="py-2 font-semibold text-gray-600">📋 Topic:</td>
                          <td className="py-2 text-gray-800">{bookingData?.title}</td>
                        </tr>
                      )}
                      {bookingData?.serviceInterest && (
                        <tr>
                          <td className="py-2 font-semibold text-gray-600">💼 Service:</td>
                          <td className="py-2 text-gray-800">{bookingData?.serviceInterest}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                
                {/* Preparation Box */}
                <div className="bg-yellow-50 p-5 rounded-lg border-l-4 border-yellow-400 my-6">
                  <h4 className="text-yellow-800 text-base font-semibold mb-3">📝 Before Your Consultation</h4>
                  <ul className="list-disc list-inside space-y-2 text-yellow-800 text-sm">
                    <li>You'll receive a calendar invite shortly</li>
                    <li>Prepare any questions or materials you'd like to discuss</li>
                    <li>We'll send a meeting link 24 hours before the appointment</li>
                  </ul>
                </div>
                
                <p className="text-gray-800 text-base my-6">
                  If you need to reschedule or have any questions, please don't hesitate to reach out.
                </p>
                
                {/* CTA Button */}
                <div className="text-center my-8">
                  <div className="inline-block bg-gradient-to-r from-purple-600 to-purple-800 text-white px-8 py-3 rounded-lg font-bold text-base">
                    View Dashboard
                  </div>
                </div>
                
                {/* Footer */}
                <div className="text-sm text-gray-600 mt-8 pt-6 border-t border-gray-300">
                  <p>
                    Looking forward to speaking with you!<br />
                    <strong>The Team</strong>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Collapsed State Info */}
          {!isExpanded && (
            <div className="p-6 bg-gray-50 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Icon name="CheckCircle" size={20} className="text-green-600" />
                  <p className="text-gray-700">
                    Confirmation email sent successfully to <strong>{bookingData?.clientEmail}</strong>
                  </p>
                </div>
                <button
                  onClick={() => setIsExpanded(true)}
                  className="text-[#1FB5A5] hover:text-[#1FB5A5]/80 font-semibold text-sm flex items-center space-x-1 transition-colors"
                >
                  <span>View Email</span>
                  <Icon name="ChevronDown" size={16} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Email Tips */}
        <div className="mt-6 bg-white/10 backdrop-blur-sm rounded-xl p-6">
          <div className="flex items-start space-x-3">
            <Icon name="Info" size={20} className="text-[#1FB5A5] mt-1 flex-shrink-0" />
            <div className="text-white">
              <p className="font-semibold mb-2">Email Delivery Information</p>
              <ul className="text-sm text-white/80 space-y-1">
                <li>• Check your spam/junk folder if you don't see the email within 5 minutes</li>
                <li>• Add our email to your contacts to ensure future emails arrive in your inbox</li>
                <li>• You'll receive reminder emails 24 hours and 1 hour before your consultation</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EmailPreview;